import React, { useState, useMemo, useEffect } from 'react';
import {
  Coins, Search, X, Eye, CheckSquare, Square, Receipt, ChevronDown, ChevronUp
} from 'lucide-react';

// 状态徽章
const StatusBadge = ({ status, type }) => {
  const colorMap = {
    // 结算状态
    '待审核': 'bg-purple-100 text-purple-700',
    '待确认': 'bg-amber-100 text-amber-700',
    '已确认': 'bg-green-100 text-green-700',
    // 开票状态
    '已开票': 'bg-teal-100 text-teal-700',
    '未开票': 'bg-slate-100 text-slate-700',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${colorMap[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

// 批量开票弹窗 - 按供应商分组显示待开票的结算单
const BatchInvoiceModal = ({ isOpen, onClose, onConfirm, settlements, onToggleSelect, selectedIds }) => {
  // 按供应商分组
  const groupedBySupplier = useMemo(() => {
    const groups = {};
    settlements.forEach(doc => {
      if (!groups[doc.supplierName]) {
        groups[doc.supplierName] = [];
      }
      groups[doc.supplierName].push(doc);
    });
    return groups;
  }, [settlements]);

  const selectedDocs = settlements.filter(doc => selectedIds.includes(doc.id));
  const totalAmount = selectedDocs.reduce((sum, doc) => sum + (doc.amount || 0), 0);
  const totalWeight = selectedDocs.reduce((sum, doc) => sum + (doc.actualWeight || 0), 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-bold text-slate-800">批量开票</h3>
            <p className="text-sm text-slate-500 mt-1">按供应商分组显示待开票的结算单</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto p-6">
          {Object.keys(groupedBySupplier).length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Coins size={48} className="mx-auto text-slate-300 mb-4" />
              <p>暂无待开票的结算单</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedBySupplier).map(([supplier, docs]) => {
                const supplierSelectedIds = docs.filter(d => selectedIds.includes(d.id)).map(d => d.id);
                const isAllSelected = supplierSelectedIds.length === docs.length;
                const supplierTotal = docs.reduce((sum, doc) => sum + (doc.amount || 0), 0);
                const supplierWeight = docs.reduce((sum, doc) => sum + (doc.actualWeight || 0), 0);

                return (
                  <div key={supplier} className="border border-slate-200 rounded-lg overflow-hidden">
                    {/* 供应商标题栏 */}
                    <div className="bg-slate-50 px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => {
                            docs.forEach(doc => {
                              onToggleSelect(doc.id, !isAllSelected);
                            });
                          }}
                          className={`p-1 rounded ${isAllSelected ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                          {isAllSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                        </button>
                        <span className="font-medium text-slate-800">{supplier}</span>
                        <span className="text-sm text-slate-500">{docs.length}条结算单</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-500">总重量: </span>
                        <span className="font-medium text-slate-700">{supplierWeight.toFixed(2)}吨</span>
                        <span className="text-slate-400 mx-2">|</span>
                        <span className="text-slate-500">总金额: </span>
                        <span className="font-medium text-red-600">¥{supplierTotal.toLocaleString()}</span>
                      </div>
                    </div>

                    {/* 结算单列表 */}
                    <div className="divide-y divide-slate-100">
                      {docs.map(doc => {
                        const isSelected = selectedIds.includes(doc.id);
                        return (
                          <div
                            key={doc.id}
                            onClick={() => onToggleSelect(doc.id, !isSelected)}
                            className={`px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 ${isSelected ? 'bg-blue-50' : ''}`}
                          >
                            <div className="flex items-center gap-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onToggleSelect(doc.id, !isSelected);
                                }}
                                className={`p-1 rounded ${isSelected ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                              >
                                {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                              </button>
                              <div>
                                <span className="font-mono text-xs text-slate-600">{doc.docNo}</span>
                                {doc.isMerged && (
                                  <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-600 text-[10px] rounded">合并</span>
                                )}
                              </div>
                            </div>
                            <div className="text-sm">
                              <span className="text-slate-500">{doc.actualWeight}吨</span>
                              <span className="text-slate-400 mx-2">×</span>
                              <span className="text-slate-500">¥{doc.unitPrice}/吨</span>
                              <span className="text-slate-400 mx-2">=</span>
                              <span className="font-medium text-red-600">¥{doc.amount?.toLocaleString()}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 底部操作栏 */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">
              已选择 <span className="font-medium text-slate-800">{selectedDocs.length}</span> 条结算单，
              总重量 <span className="font-medium text-slate-800">{totalWeight.toFixed(2)}吨</span>，
              总金额 <span className="font-medium text-red-600">¥{totalAmount.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={() => {
                  onConfirm(selectedDocs);
                  onClose();
                }}
                disabled={selectedDocs.length === 0}
                className="px-4 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
              >
                确认生成发票单
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SettlementManageView = ({ deliveries = [], orders = [], onDetailClick, onGenerateDocument, onStockInClick, initialFilter, setInitialFilter }) => {
  // 筛选状态 - 初始化时如果有 initialFilter 则使用，否则为空
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStatus, setFilterStatus] = useState(() => {
    if (!initialFilter?.status) return '';
    return Array.isArray(initialFilter.status) ? initialFilter.status.join(',') : initialFilter.status;
  });
  const [filterInvoiceStatus, setFilterInvoiceStatus] = useState(() => initialFilter?.invoiceStatus || '');

  // 批量开票状态
  const [showBatchInvoiceModal, setShowBatchInvoiceModal] = useState(false);
  const [batchInvoiceSelectedIds, setBatchInvoiceSelectedIds] = useState([]);

  // 展开的入库单行
  const [expandedStockInRows, setExpandedStockInRows] = useState([]);

  // 处理从首页传递的初始筛选条件（仅在 initialFilter 变化时更新）
  useEffect(() => {
    if (initialFilter) {
      if (initialFilter.status !== undefined) {
        const newStatus = Array.isArray(initialFilter.status)
          ? initialFilter.status.join(',')
          : initialFilter.status;
        setFilterStatus(newStatus);
      }
      if (initialFilter.invoiceStatus !== undefined) {
        setFilterInvoiceStatus(initialFilter.invoiceStatus);
      }
      // 清除 initialFilter 表示已处理
      setInitialFilter?.(null);
    }
  }, [initialFilter, setInitialFilter]);

  // 切换入库单展开状态
  const toggleStockInExpand = (docId) => {
    setExpandedStockInRows(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  // 从 deliveries 提取结算单数据（按结算单号聚合，合并单只显示一行）
  const settlements = useMemo(() => {
    const groups = {};

    deliveries.forEach(delivery => {
      if (delivery.settlement) {
        const settlementNo = delivery.settlement.settlementNo;

        if (!groups[settlementNo]) {
          // 结算状态：待审核 → 待确认 → 已确认
          const isApproved = delivery.settlement.approved !== false;
          const isConfirmed = delivery.settlement.confirmedBySupplier === true;
          let settlementStatus;
          if (!isApproved) {
            settlementStatus = '待审核';
          } else if (isConfirmed) {
            settlementStatus = '已确认';
          } else {
            settlementStatus = '待确认';
          }
          const invoiceStatus = delivery.invoice ? '已开票' : '未开票';

          groups[settlementNo] = {
            id: settlementNo, // 使用结算单号作为唯一ID
            docNo: settlementNo,
            docType: 'settlement',
            orderIds: [],
            orderNos: [],
            deliveryIds: [],
            deliveryNos: [],
            stockInNos: [],
            supplierId: delivery.supplierId,
            supplierName: delivery.supplierName,
            baseId: delivery.baseId,
            baseName: delivery.baseName,
            materialId: delivery.materialId,
            materialName: delivery.materialName,
            actualWeight: 0,
            unitPrice: delivery.settlement.unitPrice,
            amount: 0,
            status: settlementStatus,
            invoiceStatus: invoiceStatus,
            confirmedAt: delivery.settlement.confirmedAt,
            createdAt: delivery.storage?.at || delivery.createdAt,
            deliveries: [], // 关联的原始送货单
          };
        }

        // 累加数据
        const group = groups[settlementNo];
        if (!group.orderIds.includes(delivery.orderId)) {
          group.orderIds.push(delivery.orderId);
          group.orderNos.push(delivery.orderNo);
        }
        group.deliveryIds.push(delivery.id);
        group.deliveryNos.push(delivery.deliveryNo);
        group.deliveries.push(delivery);

        // 获取入库单号
        const stockInNo = delivery.storage?.stockInNo;
        if (stockInNo && !group.stockInNos.includes(stockInNo)) {
          group.stockInNos.push(stockInNo);
        }

        // 累加重量和金额
        const weight = delivery.storage?.actualWeight || 0;
        const amount = delivery.settlement.amount || 0;
        group.actualWeight += weight;
        group.amount += amount;

        // 更新创建时间（取最早的）
        const createdAt = delivery.storage?.at || delivery.createdAt;
        if (createdAt && createdAt < group.createdAt) {
          group.createdAt = createdAt;
        }
      }
    });

    // 转换为数组并标记是否为合并单
    return Object.values(groups).map(group => ({
      ...group,
      actualWeight: Math.round(group.actualWeight * 100) / 100,
      amount: Math.round(group.amount * 100) / 100,
      isMerged: group.stockInNos.length >= 2,
    }));
  }, [deliveries]);

  // 获取唯一的供应商列表
  const suppliers = useMemo(() => {
    const supplierSet = new Set();
    settlements.forEach(doc => {
      if (doc.supplierName) supplierSet.add(doc.supplierName);
    });
    return Array.from(supplierSet).sort();
  }, [settlements]);

  // 筛选后的数据
  const filteredDocs = useMemo(() => {
    let result = [...settlements];

    // 搜索筛选
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(doc =>
        doc.docNo?.toLowerCase().includes(query) ||
        doc.supplierName?.toLowerCase().includes(query)
      );
    }

    // 供应商筛选
    if (filterSupplier) {
      result = result.filter(doc => doc.supplierName === filterSupplier);
    }

    // 结算状态筛选（支持多状态，用逗号分隔）
    if (filterStatus) {
      const statuses = filterStatus.split(',');
      result = result.filter(doc => statuses.includes(doc.status));
    }

    // 开票状态筛选
    if (filterInvoiceStatus) {
      result = result.filter(doc => doc.invoiceStatus === filterInvoiceStatus);
    }

    // 按时间倒序
    result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return result;
  }, [settlements, searchQuery, filterSupplier, filterStatus, filterInvoiceStatus]);

  // 统计数据
  const stats = useMemo(() => {
    const total = settlements.length;
    const pendingApproval = settlements.filter(d => d.status === '待审核').length;
    const pending = settlements.filter(d => d.status === '待确认').length;
    const confirmed = settlements.filter(d => d.status === '已确认').length;
    const totalAmount = settlements.reduce((sum, d) => sum + (d.amount || 0), 0);
    return { total, pendingApproval, pending, confirmed, totalAmount };
  }, [settlements]);

  // 获取待开票的结算单（已确认 && 未开票）
  const pendingInvoiceSettlements = useMemo(() => {
    return settlements.filter(doc => doc.status === '已确认' && doc.invoiceStatus === '未开票');
  }, [settlements]);

  // 批量开票 - 切换选择
  const toggleBatchInvoiceSelect = (docId, isSelected) => {
    setBatchInvoiceSelectedIds(prev =>
      isSelected
        ? [...prev, docId]
        : prev.filter(id => id !== docId)
    );
  };

  // 打开批量开票弹窗
  const openBatchInvoice = () => {
    setBatchInvoiceSelectedIds(pendingInvoiceSettlements.map(doc => doc.id));
    setShowBatchInvoiceModal(true);
  };

  // 确认批量生成发票单
  const confirmBatchInvoice = (selectedDocs) => {
    onGenerateDocument?.({
      sourceType: 'settlement',
      targetType: 'invoice',
      targetLabel: '发票单',
      sourceDocs: selectedDocs,
      totalAmount: selectedDocs.reduce((sum, doc) => sum + (doc.amount || 0), 0),
      totalWeight: selectedDocs.reduce((sum, doc) => sum + (doc.actualWeight || 0), 0),
      suppliers: [...new Set(selectedDocs.map(d => d.supplierName))],
      isBatch: true,
    });
    setShowBatchInvoiceModal(false);
    setBatchInvoiceSelectedIds([]);
  };

  // 清除所有筛选
  const clearFilters = () => {
    setSearchQuery('');
    setFilterSupplier('');
    setFilterStatus('');
    setFilterInvoiceStatus('');
  };

  const hasActiveFilters = searchQuery || filterSupplier || filterStatus || filterInvoiceStatus;

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">结算单管理</h1>
          <p className="text-slate-500 text-sm mt-1">管理所有结算单据</p>
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">全部结算单</p>
              <p className="text-2xl font-bold text-slate-800 mt-1">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
              <Coins size={24} className="text-orange-500" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">待审核</p>
              <p className="text-2xl font-bold text-purple-600 mt-1">{stats.pendingApproval}</p>
            </div>
            <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
              <Coins size={24} className="text-purple-500" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">待确认</p>
              <p className="text-2xl font-bold text-amber-600 mt-1">{stats.pending}</p>
            </div>
            <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
              <Coins size={24} className="text-amber-500" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">已确认</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{stats.confirmed}</p>
            </div>
            <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
              <Coins size={24} className="text-green-500" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">结算总金额</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">¥{stats.totalAmount.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
              <Coins size={24} className="text-blue-500" />
            </div>
          </div>
        </div>
      </div>

      {/* 筛选区域 */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100">
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-wrap items-center gap-3">
            {/* 搜索框 */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="搜索结算单号、供应商..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* 供应商筛选 */}
            <select
              value={filterSupplier}
              onChange={(e) => setFilterSupplier(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">全部供应商</option>
              {suppliers.map(supplier => (
                <option key={supplier} value={supplier}>{supplier}</option>
              ))}
            </select>

            {/* 结算状态筛选 */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">全部结算状态</option>
              <option value="待审核">待审核</option>
              <option value="待确认">待确认</option>
              <option value="已确认">已确认</option>
            </select>

            {/* 开票状态筛选 */}
            <select
              value={filterInvoiceStatus}
              onChange={(e) => setFilterInvoiceStatus(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">全部开票状态</option>
              <option value="未开票">未开票</option>
              <option value="已开票">已开票</option>
            </select>

            {/* 清除筛选 */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-slate-500 hover:text-slate-700"
              >
                <X size={16} />
                清除筛选
              </button>
            )}

            {/* 批量开票按钮 */}
            {pendingInvoiceSettlements.length > 0 && (
              <button
                onClick={openBatchInvoice}
                className="flex items-center gap-1 px-4 py-2.5 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors"
              >
                <Receipt size={16} />
                批量开票
                <span className="bg-white/20 text-xs px-1.5 py-0.5 rounded-full">
                  {pendingInvoiceSettlements.length}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* 数据表格 */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">结算单号</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">供应商</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">关联入库单</th>
                <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">实重(吨)</th>
                <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">单价</th>
                <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">结算金额</th>
                <th className="text-center p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">结算状态</th>
                <th className="text-center p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">开票状态</th>
                <th className="text-center p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredDocs.length === 0 ? (
                <tr>
                  <td colSpan="9" className="p-8 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-2">
                      <Coins size={40} className="text-slate-300" />
                      <p>暂无结算单数据</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredDocs.map((doc) => {
                  const isExpanded = expandedStockInRows.includes(doc.id);
                  const stockInCount = doc.stockInNos?.length || 0;

                  return (
                    <React.Fragment key={doc.id}>
                      <tr
                        className="hover:bg-slate-50 transition-colors"
                      >
                        <td className="p-3">
                          <span className="font-mono text-xs text-slate-600">{doc.docNo}</span>
                          {doc.isMerged && (
                            <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-600 text-[10px] rounded">合并</span>
                          )}
                        </td>
                        <td className="p-3">
                          <span className="text-sm text-slate-800 font-medium">{doc.supplierName}</span>
                        </td>
                        <td className="p-3">
                          {stockInCount === 0 ? (
                            <span className="text-xs text-slate-400">-</span>
                          ) : stockInCount === 1 ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onStockInClick?.(doc.stockInNos[0]);
                              }}
                              className="font-mono text-xs text-blue-600 hover:text-blue-700 hover:underline"
                            >
                              {doc.stockInNos[0]}
                            </button>
                          ) : (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onStockInClick?.(doc.stockInNos[0]);
                                }}
                                className="font-mono text-xs text-blue-600 hover:text-blue-700 hover:underline"
                              >
                                {doc.stockInNos[0]}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleStockInExpand(doc.id);
                                }}
                                className="flex items-center gap-0.5 text-xs text-slate-400 hover:text-slate-600"
                              >
                                <span>+{stockInCount - 1}</span>
                                {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          <span className="text-sm text-slate-600">{doc.actualWeight}</span>
                        </td>
                        <td className="p-3 text-right">
                          <span className="text-sm text-slate-600">¥{doc.unitPrice}</span>
                        </td>
                        <td className="p-3 text-right">
                          <span className="text-sm font-bold text-orange-600">¥{doc.amount?.toLocaleString()}</span>
                        </td>
                        <td className="p-3 text-center"><StatusBadge status={doc.status} type="settlement" /></td>
                        <td className="p-3 text-center"><StatusBadge status={doc.invoiceStatus} type="invoice_status" /></td>
                        <td className="p-3 text-center">
                          <button
                            onClick={() => onDetailClick?.(doc)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                          >
                            <Eye size={12} />
                            详情
                          </button>
                        </td>
                      </tr>
                      {/* 展开显示多个入库单 */}
                      {stockInCount > 1 && isExpanded && (
                        <tr className="bg-slate-50/50">
                          <td colSpan="9" className="p-0">
                            <div className="px-12 py-2 flex flex-wrap gap-2">
                              {doc.stockInNos.map((no, idx) => (
                                <button
                                  key={no}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onStockInClick?.(no);
                                  }}
                                  className="inline-flex items-center px-2 py-1 bg-white border border-slate-200 rounded text-xs font-mono text-blue-600 hover:text-blue-700 hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer"
                                >
                                  {idx === 0 && <span className="text-green-500 mr-1">●</span>}
                                  {no}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* 底部统计 */}
        {filteredDocs.length > 0 && (
          <div className="p-4 border-t border-slate-100 bg-slate-50">
            <p className="text-sm text-slate-500">
              共 <span className="font-medium text-slate-700">{filteredDocs.length}</span> 条记录
              {hasActiveFilters && (
                <span className="ml-2">
                  （筛选自 <span className="font-medium text-slate-700">{settlements.length}</span> 条）
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* 批量开票弹窗 */}
      <BatchInvoiceModal
        isOpen={showBatchInvoiceModal}
        onClose={() => {
          setShowBatchInvoiceModal(false);
          setBatchInvoiceSelectedIds([]);
        }}
        onConfirm={confirmBatchInvoice}
        settlements={pendingInvoiceSettlements}
        onToggleSelect={toggleBatchInvoiceSelect}
        selectedIds={batchInvoiceSelectedIds}
      />
    </div>
  );
};

export default SettlementManageView;
